import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { denormalize } from 'normalizr';
import { noop } from 'lodash';
import { SCHEMAS } from 'js/api/schemas';
import {
  listAssessmentHistorySummaries,
  listOrgSnapshotsForSource,
  load as loadOrg,
  lookupOrgSnapshotCompare,
} from 'js/actions/org-actions';
import {
  selectAssessmentHistorySummaries,
  selectOrgSnapshotCompareQuery,
  selectOrgSnapshotSetQuery,
} from 'js/store/reducers/queries';
import OrgSnapshotSetDetail from '../components/OrgSnapshots/OrgSnapshotSetDetail';

function asRows(result) {
  return Array.isArray(result) ? result : [];
}

function asCompare(result) {
  if (!result || Array.isArray(result)) {
    return { document: null, snapshots: [] };
  }
  return {
    document: result.document ?? null,
    snapshots: Array.isArray(result.snapshots) ? result.snapshots : [],
  };
}

function OrgSnapshotSetPageContainer({
  organization,
  workspace,
  assessmentHistorySummaries,
  activeSnapshotsQuery,
  archivedSnapshotsQuery,
  compareQuery,
  loadOrg,
  listAssessmentHistorySummaries,
  listOrgSnapshotsForSource,
  lookupOrgSnapshotCompare,
}) {
  const { orgId, sourceType, sourceKey } = useParams();
  const decodedKey = useMemo(() => decodeURIComponent(sourceKey), [sourceKey]);

  useEffect(() => {
    loadOrg(orgId).catch(noop);
    listAssessmentHistorySummaries(orgId).catch(noop);
    listOrgSnapshotsForSource(orgId, sourceType, decodedKey, {}).catch(noop);
    listOrgSnapshotsForSource(orgId, sourceType, decodedKey, {
      archived: true,
    }).catch(noop);
    lookupOrgSnapshotCompare(orgId, sourceType, decodedKey).catch(noop);
  }, [
    decodedKey,
    listAssessmentHistorySummaries,
    listOrgSnapshotsForSource,
    loadOrg,
    lookupOrgSnapshotCompare,
    orgId,
    sourceType,
  ]);

  const snapshots = asRows(activeSnapshotsQuery.data);
  const archivedSnapshots = asRows(archivedSnapshotsQuery.data);
  const compare = asCompare(compareQuery.data);
  const isLoading =
    (activeSnapshotsQuery.query.isLoading ?? false) && snapshots.length === 0;
  const isCompareLoading =
    (compareQuery.query.isLoading ?? false) && compare.snapshots.length === 0;

  return (
    <OrgSnapshotSetDetail
      organization={organization}
      workspace={workspace}
      assessmentHistorySummaries={assessmentHistorySummaries}
      snapshots={snapshots}
      archivedSnapshots={archivedSnapshots}
      compareDocument={compare.document}
      compareSnapshots={compare.snapshots}
      isLoading={isLoading}
      isCompareLoading={isCompareLoading}
      compareError={compareQuery.query.error ?? null}
    />
  );
}

export default connect(
  (store, { match }) => {
    const orgId = match?.params?.orgId;
    const workspaceId = match?.params?.workspaceId;
    const sourceType = match?.params?.sourceType;
    const sourceKey = match?.params?.sourceKey
      ? decodeURIComponent(match.params.sourceKey)
      : match?.params?.sourceKey;
    return {
      organization: denormalize(
        store.entities.organizations[orgId],
        SCHEMAS.ORGANIZATION,
        store.entities
      ),
      workspace: store.entities.workspaces[workspaceId],
      assessmentHistorySummaries:
        selectAssessmentHistorySummaries(store, orgId)?.data ?? [],
      activeSnapshotsQuery: selectOrgSnapshotSetQuery(
        store,
        orgId,
        sourceType,
        sourceKey,
        false
      ),
      archivedSnapshotsQuery: selectOrgSnapshotSetQuery(
        store,
        orgId,
        sourceType,
        sourceKey,
        true
      ),
      compareQuery: selectOrgSnapshotCompareQuery(
        store,
        orgId,
        sourceType,
        sourceKey
      ),
    };
  },
  {
    loadOrg,
    listAssessmentHistorySummaries,
    listOrgSnapshotsForSource,
    lookupOrgSnapshotCompare,
  }
)(OrgSnapshotSetPageContainer);
